import { useContext } from "react";
import { configStore } from "../contexts/ConfigContext";
import {
  KnownMappingNodeKeys,
  type MappingNode,
} from "../interfaces/MappingNode";
import { getMappingNode, getNodesByKnownKey } from "../utils/mapping";
import { PLACEHOLDER_SUBJECT, useActiveSubject } from "./useActiveSubject";

export const useAllSubjectsMappingTree = (): MappingNode => {
  const {
    config: { mappingTree },
  } = useContext(configStore);
  if (!mappingTree)
    throw new Error(
      "useAllSubjectsMappingTree was used in a context where mappingTree is undefined",
    );
  return mappingTree;
};

export const useSafeAllSubjectsMappingTree = (): MappingNode | undefined => {
  const {
    config: { mappingTree },
  } = useContext(configStore);
  if (!mappingTree) return undefined;
  return mappingTree;
};

export const useMappingSubtree = (nodeId: string): MappingNode => {
  const mappingTree = useAllSubjectsMappingTree();
  const subtree = getMappingNode(nodeId, mappingTree);
  if (!subtree) throw new Error(`Cannot find subtree for ${nodeId} node`);
  return subtree;
};

export const useMappingTree = () => {
  const { id } = useActiveSubject();
  const allSubjectsMappingTree = useAllSubjectsMappingTree();
  if (id === PLACEHOLDER_SUBJECT.id) {
    const nodes = getNodesByKnownKey(
      allSubjectsMappingTree,
      KnownMappingNodeKeys.Subject,
    );
    if (nodes.length !== 1)
      throw new Error(
        "Subject should not be placeholder if there are more than one subject",
      );
    return nodes[0];
  }
  const node = getMappingNode(id, allSubjectsMappingTree);
  if (!node) throw new Error(`Cannot find subtree for ${id} node`);
  return node;
};

export const useSafeMappingTree = (): MappingNode | undefined => {
  const {
    config: { mappingTree },
  } = useContext(configStore);
  const activeSubject = useActiveSubject();
  if (!mappingTree || activeSubject.id === PLACEHOLDER_SUBJECT.id)
    return undefined;
  const subtree = getMappingNode(activeSubject.id, mappingTree);
  return subtree;
};
